import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { transactionsService } from '../services/transactionService'
import { useToast } from '../../view/components/ui/use-toast'
import { TransactionParams } from '../services/transactionService/transaction'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from './useAuth'

interface PageParam {
  skip: number
  applyStartDate: boolean
}

interface useTransactionsProps {
  interval?: number | false
  useInfiniteScroll?: boolean
}

export const useTransactions = ({
  interval = false,
  useInfiniteScroll,
}: useTransactionsProps) => {
  const [params, setParams] = useState<TransactionParams | undefined>({})
  const { toast } = useToast()
  const location = useLocation()
  const { user } = useAuth()

  // Determina automaticamente se deve usar infinite scroll baseado na rota
  const shouldUseInfiniteScroll =
    useInfiniteScroll ?? location.pathname === '/dashboard'

  // Hook para infinite scroll (Dashboard)
  const infiniteQuery = useInfiniteQuery({
    queryKey: [
      'transactions',
      'allTransactions',
      user?._id,
      params,
    ],
    queryFn: async ({ pageParam }: { pageParam: PageParam }) => {
      const queryParams: TransactionParams = {
        ...params,
        skip: pageParam.skip,
        limit: 30,
      }

      return transactionsService.transaction({
        ...queryParams,
        flag: 'transactions',
      })
    },
    getNextPageParam: (
      lastPage: any,
      _allPages: unknown[],
      lastPageParam: PageParam,
    ) => {
      const currentSkip = lastPage.pagination.skip
      const limit = lastPage.pagination.limit
      const total = lastPage.pagination.total
      const nextSkip = currentSkip + limit

      if (nextSkip >= total) {
        return undefined
      }

      return { skip: nextSkip, applyStartDate: lastPageParam.applyStartDate }
    },
    refetchInterval: interval,
    initialPageParam: { skip: 0, applyStartDate: true } as PageParam,
    enabled: shouldUseInfiniteScroll,
  })

  // Hook para paginação normal (outros componentes)
  const normalQuery = useQuery({
    queryKey: ['transactions', 'paginated', params],
    queryFn: () => {
      const queryParams = {
        ...params,
        limit: 20,
      }
      return transactionsService.transaction({
        ...queryParams,
        flag: 'transactions',
      })
    },
    refetchInterval: interval,
    enabled: !shouldUseInfiniteScroll,
  })

  const isError = shouldUseInfiniteScroll
    ? infiniteQuery.isError
    : normalQuery.isError

  useEffect(() => {
    if (isError) {
      toast({
        variant: 'destructive',
        title: 'Erro ao carregador os dados, tente novamente mais tarde!',
      })
    }
  }, [isError, toast])

  if (shouldUseInfiniteScroll) {
    const allTransactions =
      infiniteQuery.data?.pages.flatMap((page) => page.transactions) || []

    const transformedData = infiniteQuery.data?.pages[
      infiniteQuery.data.pages.length - 1
    ]
      ? {
        ...infiniteQuery.data.pages[infiniteQuery.data.pages.length - 1],
        transactions: allTransactions,
      }
      : undefined

    return {
      isLoading: infiniteQuery.isLoading,
      isFetching: infiniteQuery.isFetching,
      data: transformedData,
      setParams,
      fetchNextPage: infiniteQuery.fetchNextPage,
      hasNextPage: infiniteQuery.hasNextPage,
      isFetchingNextPage: infiniteQuery.isFetchingNextPage,
      refetch: infiniteQuery.refetch,
    }
  } else {
    return {
      isLoading: normalQuery.isLoading,
      isFetching: normalQuery.isFetching,
      data: normalQuery.data,
      setParams,
      fetchNextPage: undefined,
      hasNextPage: undefined,
      isFetchingNextPage: false,
    }
  }
}
